/**
 * WYSIWYG-lite editor for notice emails. Bettina-proof: no raw HTML on
 * screen — a contentEditable surface styled like the email itself, a
 * two-button toolbar (bold/italic), and optional click-to-insert placeholder
 * chips with human labels. Emits innerHTML upward on every input.
 */

import { useEffect, useRef } from 'react';

export interface PlaceholderChip {
  token: string;   // "{{zip}}"
  label: string;   // "Zip code"
}

export function NoticeEditor({ html, onChange, chips, minHeight = 200 }: {
  html: string;
  onChange: (html: string) => void;
  chips?: PlaceholderChip[];
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Sync DOM from prop only when they genuinely differ (initial seed and
  // external resets). While the user types, onInput keeps prop === DOM, so
  // this never clobbers the cursor.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== html) ref.current.innerHTML = html;
  }, [html]);

  const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };

  const exec = (cmd: 'bold' | 'italic') => {
    ref.current?.focus();
    document.execCommand(cmd);
    emit();
  };

  const insertToken = (token: string) => {
    ref.current?.focus();
    document.execCommand('insertText', false, token);
    emit();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        <button className="btn btn-sm" style={{ fontWeight: 700, width: 30 }} title="Bold selected text"
          onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}>B</button>
        <button className="btn btn-sm" style={{ fontStyle: 'italic', width: 30 }} title="Italicize selected text"
          onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}>I</button>
        {chips && chips.length > 0 && (
          <>
            <span className="faint" style={{ fontSize: 11, marginLeft: 6 }}>Insert:</span>
            {chips.map((c) => (
              <button
                key={c.token}
                className="btn btn-sm"
                style={{ fontSize: 11, background: 'var(--surface-2)' }}
                title={`Inserts ${c.token} — replaced with the real value on each case`}
                onMouseDown={(e) => { e.preventDefault(); insertToken(c.token); }}
              >
                {c.label}
              </button>
            ))}
          </>
        )}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        style={{
          border: '1px solid var(--border)', borderRadius: 6, padding: '12px 16px',
          fontSize: 13, lineHeight: 1.55, background: 'var(--surface)',
          minHeight, outline: 'none', overflowY: 'auto',
        }}
      />
    </div>
  );
}
